import { useCallback, useEffect, useState } from 'react';
import { Tab, Box, Divider, Typography, LinearProgress } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import Grid from '@mui/material/Grid';

import { useApi } from '@/providers/ApiContext';
import { getDocumentAsset } from '@/actions/documentAsset';
import InspectionDocumentManifestChecks, {
  type InspectionCheckResult,
} from './InspectionDocumentManifestChecks';
import INSPECTION_STATUSES from '@/components/inspectionStatuses';
import InspectionResultGauge from './InspectionResultGauge';
import InspectionCheckReviewDrawer from './InspectionCheckReviewDrawer';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import { fetchAllInspectionChecksForManifest } from '@/util/fetchAllInspectionChecksForManifest';
import type {
  Inspection,
  Manifest,
  EntityId,
  InspectionCheck,
} from '@/types/apiTypes';
import type { INSPECTION_TYPES } from '@/components/inspectionTypes';

export interface InspectionReviewSequenceItem {
  manifest_index: number;
  row: InspectionCheck;
}

interface InspectionManifestMeta {
  manifest_id?: EntityId;
  manifest_name?: string;
  reference_document_content_id?: string;
}

interface InspectionDocumentProps {
  inspection: Inspection & {
    status: string;
    type: INSPECTION_TYPES;
    critical_non_compliant?: number;
    non_critical_non_compliant?: number;
    critical_compliant?: number;
    non_critical_compliant?: number;
    manifests?: InspectionManifestMeta[];
    [key: string]: unknown;
  };
  document?: {
    document_entity_id?: string;
    document_name?: string;
    document_doc_id?: string;
    document_content_id?: string;
    document_id?: string;
    critical_non_compliant?: number;
    non_critical_non_compliant?: number;
    critical_compliant?: number;
    non_critical_compliant?: number;
    [key: string]: unknown;
  } | null;
  manifests?: (Manifest & {
    checks?: Array<{
      section?: string;
      name: string;
      entity_id?: EntityId;
      reference?: string;
      [key: string]: unknown;
    }>;
    global?: boolean;
    reference_document_content?: string;
  })[];
}

function InspectionDocument({
  inspection,
  document,
  manifests,
}: InspectionDocumentProps): React.JSX.Element {
  const api = useApi();
  const [doc_content, setDocContent] = useState<string | null>(null);
  const [tab, setTab] = useState<number>(0);
  const [review_sequence, set_review_sequence] = useState<
    InspectionReviewSequenceItem[]
  >([]);
  const [review_sequence_loading, set_review_sequence_loading] =
    useState<boolean>(false);
  const [drawer_open, set_drawer_open] = useState<boolean>(false);
  const [sequence_index, set_sequence_index] = useState<number>(0);
  const [drawer_error, set_drawer_error] = useState<string | null>(null);
  // console.log(manifests);
  useEffect(() => {
    if (
      document?.document_content_id &&
      document?.document_id &&
      !doc_content &&
      api
    ) {
      getDocumentAsset(api, document.document_id, document.document_content_id)
        .then((data) => {
          setDocContent(data as unknown as string);
        })
        .catch((error) => console.log(error));
    }
  }, [document, doc_content, api]);

  const manifest_list = manifests ?? [];
  const document_entity_id = document?.document_entity_id as
    | EntityId
    | undefined;

  useEffect(() => {
    if (inspection.status !== INSPECTION_STATUSES.PENDING || !api) {
      set_review_sequence([]);
      set_review_sequence_loading(false);
      return;
    }
    let cancelled = false;
    const load_sequence = async (): Promise<void> => {
      set_review_sequence_loading(true);
      const built: InspectionReviewSequenceItem[] = [];
      const list = manifests ?? [];
      try {
        for (
          let manifest_index = 0;
          manifest_index < list.length;
          manifest_index++
        ) {
          const manifest = list[manifest_index];
          const rows = await fetchAllInspectionChecksForManifest(
            api,
            inspection.entity_id,
            document_entity_id,
            manifest.entity_id,
          );
          for (const row of rows) {
            built.push({ manifest_index, row });
          }
        }
        if (!cancelled) {
          set_review_sequence(built);
        }
      } catch (error) {
        console.log(error);
        if (!cancelled) {
          set_review_sequence([]);
        }
      } finally {
        if (!cancelled) {
          set_review_sequence_loading(false);
        }
      }
    };
    void load_sequence();
    return () => {
      cancelled = true;
    };
  }, [
    api,
    inspection.entity_id,
    inspection.status,
    document_entity_id,
    manifests,
  ]);

  const handle_tab_change = (
    _event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setTab(newValue);
  };

  const handle_pending_check_name_click = useCallback(
    (row: InspectionCheckResult) => {
      if (review_sequence_loading) {
        return;
      }
      const idx = review_sequence.findIndex(
        (item) => item.row.entity_id === row.entity_id,
      );
      if (idx < 0) {
        return;
      }
      set_sequence_index(idx);
      setTab(review_sequence[idx].manifest_index);
      set_drawer_open(true);
    },
    [review_sequence, review_sequence_loading],
  );

  const active_sequence_item = review_sequence[sequence_index] ?? null;
  const active_check = active_sequence_item?.row ?? null;
  const active_manifest =
    active_sequence_item && manifest_list[active_sequence_item.manifest_index]
      ? (manifest_list[active_sequence_item.manifest_index] as Manifest & {
          checks?: Array<{
            section?: string;
            name: string;
            entity_id?: EntityId;
            reference?: string;
            [key: string]: unknown;
          }>;
          global?: boolean;
          reference_document_content?: string;
        })
      : null;

  const drawer_manifest_label = active_manifest?.name || '—';

  const handle_drawer_saved = useCallback((updated: InspectionCheck) => {
    set_review_sequence((prev) =>
      prev.map((item) =>
        item.row.entity_id === updated.entity_id
          ? { ...item, row: updated }
          : item,
      ),
    );
  }, []);

  const handle_previous_check = useCallback(() => {
    set_sequence_index((i) => Math.max(0, i - 1));
  }, []);

  const handle_next_check = useCallback(() => {
    set_sequence_index((i) => Math.min(review_sequence.length - 1, i + 1));
  }, [review_sequence.length]);

  if (document) {
    return (
      <Box sx={{ width: '100%' }}>
        <Grid container component='dl' spacing={0} sx={{ display: 'table' }}>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Document Name:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {document?.document_name}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Document ID:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {document?.document_doc_id}
            </Typography>
          </Grid>
        </Grid>
        <Divider />
        {inspection?.status === INSPECTION_STATUSES.COMPLETE ? (
          <InspectionResultGauge
            critical_non_compliant={document?.critical_non_compliant || 0}
            non_critical_non_compliant={
              document?.non_critical_non_compliant || 0
            }
            critical_compliant={document?.critical_compliant || 0}
            non_critical_compliant={document?.non_critical_compliant || 0}
          />
        ) : undefined}
        <Divider />
        {inspection.status === INSPECTION_STATUSES.PENDING &&
        review_sequence_loading ? (
          <Box sx={{ py: 2 }}>
            <LinearProgress />
            <Typography variant='body2' sx={{ mt: 1 }}>
              Loading checks for review…
            </Typography>
          </Box>
        ) : undefined}
        <Box sx={{ width: '100%', typography: 'body1' }}>
          <TabContext value={tab.toString()}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList
                onChange={handle_tab_change}
                aria-label='Manage Manifests'
              >
                {manifest_list.map((manifest, i) => {
                  return (
                    <Tab
                      key={manifest.name}
                      label={manifest.name}
                      value={i.toString()}
                    />
                  );
                })}
              </TabList>
            </Box>

            {manifest_list.map((manifest, i) => {
              return (
                <TabPanel key={`${i}`} value={i.toString()}>
                  <InspectionDocumentManifestChecks
                    inspection={inspection}
                    document={document}
                    manifest={manifest}
                    pending_review_enabled={
                      inspection.status === INSPECTION_STATUSES.PENDING &&
                      !review_sequence_loading
                    }
                    on_pending_check_name_click={
                      handle_pending_check_name_click
                    }
                  />
                </TabPanel>
              );
            })}
          </TabContext>
        </Box>
        <InspectionCheckReviewDrawer
          open={drawer_open}
          onClose={() => set_drawer_open(false)}
          inspection_entity_id={inspection.entity_id}
          active_check={drawer_open ? active_check : null}
          manifest_label={drawer_manifest_label}
          reference_document_title={
            active_manifest?.reference_document?.name || ''
          }
          reference_document_content={
            active_manifest?.reference_document_content
          }
          has_previous={sequence_index > 0}
          has_next={sequence_index < review_sequence.length - 1}
          on_previous={handle_previous_check}
          on_next={handle_next_check}
          on_saved={handle_drawer_saved}
          on_error={(message) => set_drawer_error(message)}
        />
        <ErrorSnackBar
          message={drawer_error}
          onClear={() => set_drawer_error(null)}
        />
      </Box>
    );
  }
  console.log(active_manifest);
  return (
    <Box sx={{ width: '100%' }}>
      {inspection?.status === INSPECTION_STATUSES.COMPLETE ? (
        <InspectionResultGauge
          critical_non_compliant={inspection?.critical_non_compliant || 0}
          non_critical_non_compliant={
            inspection?.non_critical_non_compliant || 0
          }
          critical_compliant={inspection?.critical_compliant || 0}
          non_critical_compliant={inspection?.non_critical_compliant || 0}
        />
      ) : undefined}
      <Divider />
      {inspection.status === INSPECTION_STATUSES.PENDING &&
      review_sequence_loading ? (
        <Box sx={{ py: 2 }}>
          <LinearProgress />
          <Typography variant='body2' sx={{ mt: 1 }}>
            Loading checks for review…
          </Typography>
        </Box>
      ) : undefined}
      <Box sx={{ width: '100%', typography: 'body1' }}>
        <TabContext value={tab.toString()}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handle_tab_change} aria-label='Manage Manifests'>
              {manifest_list.map((manifest, i) => {
                return (
                  <Tab
                    key={manifest.name}
                    label={manifest.name}
                    value={i.toString()}
                  />
                );
              })}
            </TabList>
          </Box>

          {manifest_list.map((manifest, i) => {
            return (
              <TabPanel key={`${i}`} value={i.toString()}>
                <InspectionDocumentManifestChecks
                  inspection={inspection}
                  manifest={manifest}
                  pending_review_enabled={
                    inspection.status === INSPECTION_STATUSES.PENDING &&
                    !review_sequence_loading
                  }
                  on_pending_check_name_click={handle_pending_check_name_click}
                />
              </TabPanel>
            );
          })}
        </TabContext>
      </Box>
      <InspectionCheckReviewDrawer
        open={drawer_open}
        onClose={() => set_drawer_open(false)}
        inspection_entity_id={inspection.entity_id}
        active_check={drawer_open ? active_check : null}
        manifest_label={drawer_manifest_label}
        reference_document_title={
          active_manifest?.reference_document?.name || ''
        }
        reference_document_content={active_manifest?.reference_document_content}
        has_previous={sequence_index > 0}
        has_next={sequence_index < review_sequence.length - 1}
        on_previous={handle_previous_check}
        on_next={handle_next_check}
        on_saved={handle_drawer_saved}
        on_error={(message) => set_drawer_error(message)}
      />
      <ErrorSnackBar
        message={drawer_error}
        onClear={() => set_drawer_error(null)}
      />
    </Box>
  );
}

export default InspectionDocument;
